export const BLOG_LIST_URL =
  "https://backend.einsured.ca/wp-json/api/v1/ei/posts";

export const BLOG_SINGLE_URL =
  "https://backend.einsured.ca/wp-json/api/v1/ei/post";

const WP_V2_BY_SLUG =
  "https://backend.einsured.ca/wp-json/wp/v2/posts?slug=";

const WP_V2_LIST =
  "https://backend.einsured.ca/wp-json/wp/v2/posts";

const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const decodeHtml = (value = "") => {
  try {
    const doc = new DOMParser().parseFromString(
      String(value),
      "text/html",
    );

    return doc.documentElement.textContent || "";
  } catch {
    return String(value);
  }
};

const extractAuthorName = (post = {}) => {
  const authorName =
    post?._embedded?.author?.[0]?.name ||
    post?.author?.[0]?.name ||
    post?.author?.name ||
    post?.author?.display_name ||
    post?.author?.displayName ||
    post?.author_name ||
    post?.authorName ||
    post?.post_author_name ||
    post?.display_name ||
    (typeof post?.author === "string"
      ? post.author
      : "");

  return decodeHtml(authorName || "").trim();
};

const extractPublishedDate = (post = {}) =>
  post?.date ||
  post?.post_date ||
  post?.published_date ||
  post?.publishedAt ||
  post?.created_at ||
  "";

async function fetchJsonTolerant(url) {
  const response = await fetch(url, {
    cache: "no-store",
  });

  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `Blog API error ${response.status}. Response starts with: ${text.slice(
        0,
        120,
      )}`,
    );
  }

  const firstObjectIndex = text.indexOf("{");
  const firstArrayIndex = text.indexOf("[");

  const jsonStartIndex =
    firstObjectIndex === -1
      ? firstArrayIndex
      : firstArrayIndex === -1
        ? firstObjectIndex
        : Math.min(
            firstObjectIndex,
            firstArrayIndex,
          );

  if (jsonStartIndex === -1) {
    throw new Error(
      `Blog API returned non-JSON. Response starts with: ${text.slice(
        0,
        120,
      )}`,
    );
  }

  const cleanedText = text.slice(jsonStartIndex);

  try {
    return JSON.parse(cleanedText);
  } catch {
    throw new Error(
      `Blog API returned broken JSON (likely WP debug output). Starts with: ${text
        .slice(0, 120)
        .replace(/\s+/g, " ")}`,
    );
  }
}

export function normalizeListPost(post = {}) {
  return {
    id: post?.id,
    name: post?.name || "",
    title: decodeHtml(post?.title || ""),
    excerpt: stripHtml(
      decodeHtml(post?.excerpt || ""),
    ),
    date: extractPublishedDate(post),
    authorName: extractAuthorName(post),
    image:
      post?.attachment_image?.img_src?.[0] ||
      "",
    imageSrcSet:
      post?.attachment_image?.img_srcset || "",
    categories: post?.categories || {},
  };
}

function normalizeWpV2ListPost(post = {}) {
  const featuredMedia =
    post?._embedded?.["wp:featuredmedia"]?.[0];
  const featuredImage =
    featuredMedia?.source_url ||
    post?.yoast_head_json?.og_image?.[0]?.url ||
    "";
  // Resized copies WordPress already generated (skip the square thumbnail crop).
  const imageSrcSet = Object.entries(
    featuredMedia?.media_details?.sizes || {},
  )
    .filter(([name]) => name !== "thumbnail")
    .map(([, size]) => `${size.source_url} ${size.width}w`)
    .join(", ");

  return {
    id: post?.id,
    name: post?.slug || "",
    title: decodeHtml(
      post?.title?.rendered || "",
    ),
    excerpt: stripHtml(
      decodeHtml(
        post?.excerpt?.rendered || "",
      ),
    ),
    date: extractPublishedDate(post),
    authorName: extractAuthorName(post),
    image: featuredImage,
    imageSrcSet,
    categories: post?.categories || {},
  };
}

function normalizeCustomSinglePost(
  json = {},
  name = "",
) {
  return {
    id: json?.id,
    name,
    title: decodeHtml(json?.title || ""),
    html: json?.html || "",
    featuredImage:
      json?.featured_image?.img_src?.[0] ||
      json?.featuredImage ||
      "",
    date: extractPublishedDate(json),
    authorName: extractAuthorName(json),
    prev: json?.prev || null,
    next: json?.next || null,
    metaTitle: decodeHtml(
      json?.metaTitle ||
        json?.meta_title ||
        "",
    ),
    metaDesc: decodeHtml(
      json?.metaDesc ||
        json?.meta_description ||
        "",
    ),
  };
}

async function fetchWpV2AllPosts() {
  const perPage = 20;
  // Fetch pages in parallel batches instead of one request at a time.
  // The old one-page-then-wait loop was the exact cause of PageSpeed's
  // "Avoid chaining critical requests" finding (a 6-request-deep serial
  // chain, 13s+ max critical path latency) since every page load awaited
  // every WP page sequentially before the next request even started.
  const batchSize = 5;
  let page = 1;
  let reachedEnd = false;
  const allPosts = [];

  while (!reachedEnd) {
    const pageUrls = Array.from(
      { length: batchSize },
      (_, i) =>
        `${WP_V2_LIST}?per_page=${perPage}&page=${page + i}` +
        "&_embed=wp:featuredmedia" +
        "&_fields=id,slug,date,title,excerpt,categories,_links,_embedded,yoast_head_json.og_image",
    );

    const responses = await Promise.all(
      pageUrls.map((url) =>
        fetchJsonTolerant(url).catch(() => []),
      ),
    );

    for (const response of responses) {
      if (
        !Array.isArray(response) ||
        response.length === 0
      ) {
        reachedEnd = true;
        break;
      }

      allPosts.push(
        ...response.map(normalizeWpV2ListPost),
      );

      if (response.length < perPage) {
        reachedEnd = true;
        break;
      }
    }

    page += batchSize;

    // Safety against accidental infinite requests.
    if (page > 200) {
      reachedEnd = true;
    }
  }

  return allPosts.filter(
    (post) => post?.name,
  );
}

export async function fetchBlogList() {
  try {
    const wpPosts =
      await fetchWpV2AllPosts();

    if (
      Array.isArray(wpPosts) &&
      wpPosts.length > 0
    ) {
      return wpPosts;
    }
  } catch {
    // Use the custom blog API as fallback.
  }

  const response = await fetchJsonTolerant(
    BLOG_LIST_URL,
  );

  const rows = response?.posts_data || [];

  return Array.isArray(rows)
    ? rows.map(normalizeListPost)
    : [];
}

async function fetchWpV2BySlug(slug) {
  const url =
    `${WP_V2_BY_SLUG}${encodeURIComponent(
      slug,
    )}` + "&_embed=1";

  const response =
    await fetchJsonTolerant(url);

  const post = Array.isArray(response)
    ? response[0]
    : null;

  if (!post) {
    throw new Error("Post not found.");
  }

  const featuredImage =
    post?._embedded?.["wp:featuredmedia"]?.[0]
      ?.source_url ||
    post?.yoast_head_json?.og_image?.[0]?.url ||
    "";

  return {
    id: post?.id,
    name: post?.slug || slug,
    title: decodeHtml(
      post?.title?.rendered || "",
    ),
    html: post?.content?.rendered || "",
    featuredImage,
    date: extractPublishedDate(post),
    authorName: extractAuthorName(post),
    prev: null,
    next: null,
    metaTitle: decodeHtml(
      post?.yoast_head_json?.title || "",
    ),
    metaDesc: decodeHtml(
      post?.yoast_head_json?.description || "",
    ),
  };
}

export async function fetchBlogByName(
  name,
  expectedId,
) {
  try {
    const url =
      `${BLOG_SINGLE_URL}?name=` +
      encodeURIComponent(name);

    const response =
      await fetchJsonTolerant(url);

    if (
      expectedId &&
      response?.id &&
      Number(response.id) !==
        Number(expectedId)
    ) {
      return await fetchWpV2BySlug(name);
    }

    const customPost =
      normalizeCustomSinglePost(
        response,
        name,
      );

    /*
     * The custom endpoint may not return author/date.
     * Fill missing information from WordPress V2.
     */
    if (
      !customPost.authorName ||
      !customPost.date
    ) {
      try {
        const wpPost =
          await fetchWpV2BySlug(name);

        return {
          ...customPost,

          id: customPost.id || wpPost.id,

          name:
            customPost.name || wpPost.name,

          title:
            customPost.title || wpPost.title,

          html:
            customPost.html || wpPost.html,

          featuredImage:
            customPost.featuredImage ||
            wpPost.featuredImage,

          date:
            customPost.date || wpPost.date,

          authorName:
            customPost.authorName ||
            wpPost.authorName,

          metaTitle:
            customPost.metaTitle ||
            wpPost.metaTitle,

          metaDesc:
            customPost.metaDesc ||
            wpPost.metaDesc,
        };
      } catch {
        return customPost;
      }
    }

    return customPost;
  } catch {
    return await fetchWpV2BySlug(name);
  }
}